import * as React from 'react';

import { DataProxy } from 'apollo-cache';
import { withRouter } from 'app/context/Router';
import { SyncType } from 'globalTypes';
import { Mutation, Query } from 'react-apollo';
import { InjectedRouter } from 'react-router';
import { findMatching } from 'utils/arrayHelpers';

import AdminDistrictSisDashboard from 'admin/rostering/components/AdminDistrictSisDashboard';
import ErrorReporter from 'app/components/ErrorReporter';
import LoadingIndicator from 'components/LoadingIndicator';

import ADMIN_DISTRICT_SIS_DASHBOARD_QUERY from 'admin/rostering/queries/AdminDistrictSisDashboardQuery.gql';
import SET_ROSTERING_ENFORCEMENT from 'admin/rostering/mutations/SetRosteringEnforcementMutation.gql';
import TRIGGER_SYNC from 'admin/rostering/mutations/TriggerRosteringSyncMutation.gql';

import { AdminDistrictSisDashboard as AdminDistrictSisDashboardQuery } from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';
import {
  setRosteringEnforcement as setRosteringEnforcementResult,
  setRosteringEnforcementVariables,
} from 'admin/rostering/mutations/__generated__/setRosteringEnforcement';
import {
  TriggerRosteringSyncMutation,
  TriggerRosteringSyncMutationVariables,
} from 'admin/rostering/mutations/__generated__/TriggerRosteringSyncMutation';

type Props = {
  router: InjectedRouter;
  orgId: number;
};

class AdminDistrictSisDashboardContainer extends React.Component<Props> {
  render() {
    const { orgId, router } = this.props;
    const queryVars = { organizationId: orgId };

    return (
      <Mutation<setRosteringEnforcementResult>
        mutation={SET_ROSTERING_ENFORCEMENT}
        update={(cache: DataProxy, { data }) => {
          const { organizations } = cache.readQuery({
            query: ADMIN_DISTRICT_SIS_DASHBOARD_QUERY,
            variables: queryVars,
          }) as AdminDistrictSisDashboardQuery;
          const organization = findMatching(organizations, orgId, 'id');

          if (!organization || !data) {
            return;
          }

          cache.writeQuery({
            query: ADMIN_DISTRICT_SIS_DASHBOARD_QUERY,
            variables: queryVars,
            data: {
              organizations: [
                {
                  ...organization,
                  permissions: {
                    canRosteredUsersLeaveOrg: data.setRosteringEnforcement.allowedToLeave,
                    __typename: 'OrganizationPermissions',
                  },
                },
              ],
            },
          });
        }}>
        {(
          setRosteringEnforcement: (input: { variables: setRosteringEnforcementVariables }) => void
        ) => {
          return (
            <Query<AdminDistrictSisDashboardQuery>
              query={ADMIN_DISTRICT_SIS_DASHBOARD_QUERY}
              variables={queryVars}>
              {({ loading, error, data }) => {
                if (loading) {
                  return <LoadingIndicator height={400} size="small" />;
                }

                if (error || !data || !data.organizations) {
                  return (
                    <ErrorReporter error="AdminDistrictSisDashboardContainer - could not load district" />
                  );
                }

                const organization = findMatching(data.organizations, orgId, 'id');

                if (!organization) {
                  return (
                    <ErrorReporter error="AdminDistrictSisDashboardContainer - could not load district" />
                  );
                }

                const {
                  sisSyncOverview: { districtStatus },
                } = organization;

                if (!districtStatus) {
                  return (
                    <ErrorReporter error="AdminDistrictSisDashboardContainer - could not load district status" />
                  );
                }

                const toggleChangeRosteringEnforcement = (allowedToLeave: boolean) => {
                  setRosteringEnforcement({
                    variables: { input: { orgUuid: organization.uuid, allowedToLeave } },
                  });
                };

                return (
                  <Mutation<TriggerRosteringSyncMutation, TriggerRosteringSyncMutationVariables>
                    mutation={TRIGGER_SYNC}>
                    {triggerRosteringSync => {
                      return (
                        <AdminDistrictSisDashboard
                          canRosteredUsersLeaveOrg={
                            organization.permissions.canRosteredUsersLeaveOrg
                          }
                          childOrganizations={organization.childOrganizations}
                          districtStatus={districtStatus}
                          orgUuid={organization.uuid}
                          rosteringPermissions={organization.rosteringPermissions}
                          router={router}
                          handleRosteringEnforcementChange={toggleChangeRosteringEnforcement}
                          fetchNewCleverData={orgUuid => {
                            triggerRosteringSync({ variables: { districtUuid: orgUuid } });
                          }}
                        />
                      );
                    }}
                  </Mutation>
                );
              }}
            </Query>
          );
        }}
      </Mutation>
    );
  }
}

export { AdminDistrictSisDashboardContainer };
export default withRouter(AdminDistrictSisDashboardContainer);
